import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import PageHeading from '../../components/PageHeading';
import { FileTextOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import {
  Card,
  Row,
  Col,
  Select,
  Button,
  Input,
  message,
  Spin,
  Table,
  Typography,
  Space,
  Alert
} from 'antd';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import getamLogo from '../../assets/images/logo.png';

const { Text, Title } = Typography;

const ShowBookingSummary = () => {
  const { showId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const showData = state?.showData;

  const [dates, setDates] = useState(() => {
    if (showData?.dates && showData.dates.length > 0) {
      return showData.dates.map(d => ({
        label: d.date,
        value: d.id
      }));
    }
    return [];
  });

  const [timeslots, setTimeslots] = useState([]);
  const [selectedDateId, setSelectedDateId] = useState(null);
  const [selectedTimeslotId, setSelectedTimeslotId] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [gutschriftsbetragBrutto, setGutschriftsbetragBrutto] = useState(0);
  const [mwst, setMwst] = useState(0);
  const [gutschriftsbetragNetto, setGutschriftsbetragNetto] = useState(0);

  // Editable free tickets
  const [editableFreeTickets, setEditableFreeTickets] = useState(0);

  // Editable fields
  const [organizerName, setOrganizerName] = useState('');
  const [organizerVat, setOrganizerVat] = useState('');
  const [editableMovieName, setEditableMovieName] = useState(showData?.movie_name || '');
  const [editableTheatreName, setEditableTheatreName] = useState(showData?.theatre_name || '');
  const [editableShowDate, setEditableShowDate] = useState('');
  const [editableShowTime, setEditableShowTime] = useState('');
  const [billDate, setBillDate] = useState(() => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const yyyy = now.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  });

  // Service Provider fields
  const [providerAddress1, setProviderAddress1] = useState('');
  const [providerAddress2, setProviderAddress2] = useState('');
  const [providerVat, setProviderVat] = useState('');

  // If no show data passed, show error
  if (!showData) {
    return (
      <div className="dashboard">
        <PageHeading name="Show Booking Summary" icon={<FileTextOutlined />} />
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
          Back
        </Button>
        <Alert
          message="Error"
          description="No show data available. Please navigate from the Shows page."
          type="error"
          showIcon
        />
      </div>
    );
  }

  const handleDateChange = (dateId) => {
    setSelectedDateId(dateId);
    setSelectedTimeslotId(null);
    setSummaryData(null);

    // Find the selected date and its timeslots from the passed showData
    const selectedDate = showData?.dates?.find(d => d.id === dateId);
    if (selectedDate?.timeslots) {
      const activeSlots = selectedDate.timeslots.filter(ts => ts.status);
      const slotOptions = activeSlots.map(ts => {
        const label = ts.timeslot_title
          ? `${ts.timeslot_title}`
          : `Slot #${ts.id}`;
        return { label, value: ts.id };
      });
      setTimeslots(slotOptions);
    } else {
      setTimeslots([]);
    }
  };

  const handleTimeslotChange = (timeslotId) => {
    setSelectedTimeslotId(timeslotId);
    fetchSummaryData(timeslotId);
  };

  const fetchSummaryData = async (timeslotId) => {
    if (!selectedDateId || !timeslotId) return;

    setSummaryLoading(true);
    try {
      const response = await axiosInstance.get(
        `booking/show-summary/${showId}/${selectedDateId}/${timeslotId}/`
      );
      const data = response.data;
      setSummaryData(data);

      // Set editable fields from API data
      setEditableShowDate(data.show_date || '');
      setEditableShowTime(data.show_time || '');
      setEditableFreeTickets(data.seats_reserved || 0);

      // Calculate default Gutschriftsbetrag (Brutto) = total_revenue - total_service_fee
      const brutto = parseFloat(data.total_revenue || 0) - parseFloat(data.total_service_fee || 0);
      setGutschriftsbetragBrutto(brutto);
      // MWSt 7% = 7% of Brutto
      const mwstVal = brutto * 0.07;
      setMwst(mwstVal);
      // Netto = Brutto - MWSt
      setGutschriftsbetragNetto(brutto - mwstVal);
    } catch (error) {
      console.error('Error fetching summary:', error);
      message.error('Failed to load booking summary');
      setSummaryData(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleBruttoChange = (value) => {
    const parsed = parseFloat(value) || 0;
    setGutschriftsbetragBrutto(parsed);
    const mwstVal = parsed * 0.07;
    setMwst(mwstVal);
    setGutschriftsbetragNetto(parsed - mwstVal);
  };

  const handleMwstChange = (value) => {
    const parsed = parseFloat(value) || 0;
    setMwst(parsed);
    setGutschriftsbetragNetto(gutschriftsbetragBrutto - parsed);
  };

  const handleNettoChange = (value) => {
    const parsed = parseFloat(value) || 0;
    setGutschriftsbetragNetto(parsed);
    const mwstVal = gutschriftsbetragBrutto - parsed;
    setMwst(mwstVal >= 0 ? mwstVal : 0);
  };

  const formatCurrency = (value) => {
    return `€${parseFloat(value || 0).toFixed(2)}`;
  };

  const loadImageAsBase64 = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = (err) => {
        console.error('Error loading image:', err);
        reject(err);
      };
      img.src = url;
    });
  };

  const generatePDF = async () => {
    if (!summaryData) {
      message.warning('Please select a date and timeslot first');
      return;
    }

    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });

      // Try to load logo
      let logoBase64 = null;
      try {
        logoBase64 = await loadImageAsBase64(getamLogo);
      } catch (e) {
        console.warn('Logo could not be loaded, proceeding without it');
      }

      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;

      // === HEADER: RECEIPT (left) + Logo (right) ===
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('RECEIPT', margin, 25);

      if (logoBase64) {
        try {
          doc.addImage(logoBase64, 'PNG', pageWidth - margin - 50, 12, 50, 20);
        } catch (e) {
          console.warn('Could not add image to PDF');
        }
      }

      // Divider line
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 32, pageWidth - margin, 32);

      // === INFO SECTION: Two columns ===
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      let leftX = margin;
      let rightX = pageWidth / 2 + 5;
      let infoY = 42;
      const lineHeight = 6;

      // Left column data
      const leftData = [
        { label: 'Organizer Name', value: organizerName || '—' },
        { label: 'VAT Number', value: organizerVat || '—' },
        { label: 'Movie Name', value: editableMovieName || '—' },
        { label: 'Theatre Name', value: editableTheatreName || '—' },
        { label: 'Show Date', value: editableShowDate || '—' },
        { label: 'Show Time', value: editableShowTime || '—' },
        { label: 'Bill Date', value: billDate || '—' },
      ];

      doc.setFont('helvetica', 'bold');
      doc.text('Session Details', leftX, infoY);
      doc.setFont('helvetica', 'normal');
      infoY += lineHeight + 2;

      leftData.forEach(item => {
        doc.setFont('helvetica', 'bold');
        doc.text(`${item.label}:`, leftX, infoY);
        doc.setFont('helvetica', 'normal');
        const labelWidth = doc.getTextWidth(`${item.label}: `);
        doc.text(item.value, leftX + labelWidth + 1, infoY);
        infoY += lineHeight;
      });

      // Right column - Service Provider
      doc.setFont('helvetica', 'bold');
      doc.text('Service Provider', rightX, 42);
      doc.setFont('helvetica', 'normal');
      let rightInfoY = 42 + lineHeight + 2;

      const rightData = [
        { label: 'Address', value: providerAddress1 || '—' },
        { label: '', value: providerAddress2 || '' },
        { label: 'VAT Number', value: providerVat || '—' },
      ];

      rightData.forEach(item => {
        if (item.label) {
          doc.setFont('helvetica', 'bold');
          doc.text(`${item.label}:`, rightX, rightInfoY);
          doc.setFont('helvetica', 'normal');
          const labelWidth = doc.getTextWidth(`${item.label}: `);
          doc.text(item.value, rightX + labelWidth + 1, rightInfoY);
        } else {
          doc.text(item.value, rightX, rightInfoY);
        }
        rightInfoY += lineHeight;
      });

      // === SUMMARY TABLE ===
      const tableStartY = Math.max(infoY, rightInfoY) + 8;

      const tableHeaders = ['Metric', 'Value'];
      const tableData = [
        // ['Total Bookings', `${summaryData.total_paid_tickets || 0}`],
        ['Paid Tickets', `${summaryData.seats_booked || 0}`],
        ['Free Tickets', `${summaryData.seats_reserved || 0}`],
        ['Total Tickets', `${summaryData.total_seats || 0}`],
        ['Total Ticket  Revenue', formatCurrency(summaryData.total_revenue || 0)],
        ['Total Service Fee', formatCurrency(summaryData.total_service_fee || 0)],
      ];

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: tableStartY,
        margin: { left: margin, right: margin },
        theme: 'grid',
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10,
        },
        bodyStyles: {
          fontSize: 9,
        },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.6, fontStyle: 'bold' },
          1: { cellWidth: contentWidth * 0.4, halign: 'right' },
        },
        styles: {
          cellPadding: 3,
        },
      });

      // === COMPUTED FIELDS ===
      const lastTableY = doc.lastAutoTable.finalY + 8;

      doc.setDrawColor(200, 200, 200);
      doc.line(margin, lastTableY - 4, pageWidth - margin, lastTableY - 4);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Calculations', margin, lastTableY);

      const calcHeaders = ['Description', 'Amount'];
      const calcData = [
        ['Gutschriftsbetrag (Brutto)', formatCurrency(gutschriftsbetragBrutto)],
        ['MWSt 7%', formatCurrency(mwst)],
        ['Gutschriftsbetrag (Netto)', formatCurrency(gutschriftsbetragNetto)],
      ];

      autoTable(doc, {
        head: [calcHeaders],
        body: calcData,
        startY: lastTableY + 4,
        margin: { left: margin, right: margin },
        theme: 'grid',
        headStyles: {
          fillColor: [46, 204, 113],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10,
        },
        bodyStyles: {
          fontSize: 9,
        },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.6, fontStyle: 'bold' },
          1: { cellWidth: contentWidth * 0.4, halign: 'right', fontStyle: 'bold' },
        },
        styles: {
          cellPadding: 3,
        },
      });

      // === FOOTER ===
      const footerY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(150, 150, 150);
      doc.text('This is a computer-generated receipt. No signature required.', margin, footerY);
      doc.text(`Generated on: ${new Date().toLocaleString('en-US', { timeZone: 'UTC' })} UTC`, margin, footerY + 4);

      // Save PDF
      doc.save(`Booking_Summary_${showId}_${selectedDateId}_${selectedTimeslotId}.pdf`);
      message.success('PDF generated successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    }
  };

  const handleFreeTicketsChange = (value) => {
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      setEditableFreeTickets(parsed);
    }
  };

  const summaryColumns = [
    {
      title: 'Metric',
      dataIndex: 'label',
      key: 'label',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      align: 'right',
      render: (text, record) => {
        if (record.key === '4') {
          return (
            <Input
              type="number"
              min={0}
              value={editableFreeTickets}
              onChange={(e) => handleFreeTicketsChange(e.target.value)}
              style={{ width: 100, textAlign: 'right' }}
            />
          );
        }
        return text;
      },
    },
  ];

  const paidTickets = summaryData?.seats_booked || 0;
  const totalTickets = paidTickets + editableFreeTickets;

  const summaryTableData = summaryData
    ? [
        { key: '1', label: 'Total Bookings Count', value: summaryData.total_paid_tickets || 0 },
        { key: '2', label: 'Total Tickets', value: totalTickets },
        { key: '3', label: 'Paid Tickets', value: paidTickets },
        { key: '4', label: 'Free Tickets', value: editableFreeTickets },
        { key: '5', label: 'Total Revenue', value: formatCurrency(summaryData.total_revenue || 0) },
        { key: '6', label: 'Total Service Fee', value: formatCurrency(summaryData.total_service_fee || 0) },
      ]
    : [];

  return (
    <div className="dashboard">
      <PageHeading name="Show Booking Summary" icon={<FileTextOutlined />} />

      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        style={{ marginBottom: 16 }}
      >
        Back
      </Button>

      <Alert
        message="Instructions"
        description="Select a date and timeslot to load the booking summary. Fill in the organizer and service provider details, then click 'Generate PDF' to create the receipt."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Date & Timeslot Selection */}
      <Card title="Select Show Session" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary">Date</Text>
            </div>
            <Select
              placeholder="Select a date"
              style={{ width: '100%' }}
              options={dates}
              value={selectedDateId}
              onChange={handleDateChange}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary">Timeslot</Text>
            </div>
            <Select
              placeholder="Select a timeslot"
              style={{ width: '100%' }}
              options={timeslots}
              value={selectedTimeslotId}
              onChange={handleTimeslotChange}
              disabled={!selectedDateId || timeslots.length === 0}
              allowClear
              notFoundContent={selectedDateId ? 'No active timeslots for this date' : 'Select a date first'}
            />
          </Col>
        </Row>
      </Card>

      {summaryLoading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading summary data...</p>
        </div>
      )}

      {!summaryLoading && summaryData && (
        <>
          {/* Organizer & Session Info */}
          <Card title="Session & Organizer Details" style={{ marginBottom: 24 }}>
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <div>
                    <Text type="secondary">Organizer Name</Text>
                    <Input
                      value={organizerName}
                      onChange={(e) => setOrganizerName(e.target.value)}
                      placeholder="Enter organizer name"
                    />
                  </div>
                  <div>
                    <Text type="secondary">VAT Number</Text>
                    <Input
                      value={organizerVat}
                      onChange={(e) => setOrganizerVat(e.target.value)}
                      placeholder="Enter VAT number"
                    />
                  </div>
                  <div>
                    <Text type="secondary">Movie Name</Text>
                    <Input
                      value={editableMovieName}
                      onChange={(e) => setEditableMovieName(e.target.value)}
                    />
                  </div>
                  <div>
                    <Text type="secondary">Theatre Name</Text>
                    <Input
                      value={editableTheatreName}
                      onChange={(e) => setEditableTheatreName(e.target.value)}
                    />
                  </div>
                  <div>
                    <Text type="secondary">Show Date</Text>
                    <Input
                      value={editableShowDate}
                      onChange={(e) => setEditableShowDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Text type="secondary">Show Time</Text>
                    <Input
                      value={editableShowTime}
                      onChange={(e) => setEditableShowTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Text type="secondary">Bill Date</Text>
                    <Input
                      value={billDate}
                      onChange={(e) => setBillDate(e.target.value)}
                      placeholder="MM/DD/YYYY"
                    />
                  </div>
                </Space>
              </Col>

              <Col xs={24} md={12}>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Title level={5}>Service Provider</Title>
                  <div>
                    <Text type="secondary">Address Line 1</Text>
                    <Input
                      value={providerAddress1}
                      onChange={(e) => setProviderAddress1(e.target.value)}
                      placeholder="Street, number"
                    />
                  </div>
                  <div>
                    <Text type="secondary">Address Line 2</Text>
                    <Input
                      value={providerAddress2}
                      onChange={(e) => setProviderAddress2(e.target.value)}
                      placeholder="City, postal code"
                    />
                  </div>
                  <div>
                    <Text type="secondary">VAT Number</Text>
                    <Input
                      value={providerVat}
                      onChange={(e) => setProviderVat(e.target.value)}
                      placeholder="Enter VAT number"
                    />
                  </div>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Summary Table */}
          <Card title="Booking Summary" style={{ marginBottom: 24 }}>
            <Table
              dataSource={summaryTableData}
              columns={summaryColumns}
              pagination={false}
              bordered
              size="middle"
              showHeader={true}
              style={{ maxWidth: 500 }}
            />
          </Card>

          {/* Computed Fields */}
          <Card title="Calculated Amounts" style={{ marginBottom: 24 }}>
            <Row gutter={[24, 16]}>
              <Col xs={24} md={8}>
                <Text type="secondary">Gutschriftsbetrag (Brutto)</Text>
                <Input
                  value={gutschriftsbetragBrutto.toFixed(2)}
                  onChange={(e) => handleBruttoChange(e.target.value)}
                  suffix="EUR"
                />
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">MWSt 7%</Text>
                <Input
                  value={mwst.toFixed(2)}
                  onChange={(e) => handleMwstChange(e.target.value)}
                  suffix="EUR"
                />
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary">Gutschriftsbetrag (Netto)</Text>
                <Input
                  value={gutschriftsbetragNetto.toFixed(2)}
                  onChange={(e) => handleNettoChange(e.target.value)}
                  suffix="EUR"
                />
              </Col>
            </Row>
          </Card>

          {/* Generate PDF Button */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Button
              type="primary"
              size="large"
              icon={<FileTextOutlined />}
              onClick={generatePDF}
            >
              Generate PDF
            </Button>
          </div>
        </>
      )}

      {!summaryLoading && !summaryData && selectedDateId && (
        <Alert
          message="No Data"
          description="No summary data available for the selected session."
          type="warning"
          showIcon
        />
      )}
    </div>
  );
};

export default ShowBookingSummary;

